import React from 'react';

// Firebase
import { db } from "../../../../firebaseConfig";

// global
import { useAuth } from '../../../../AuthContext';

// redux
import { useAppSelector } from '../../../../redux/store';

// function fetchCallData
import { fetchCallMetrics } from '../../../../components/CallCRM/Function';
import getCurrentScreen from '../../../../Helper/GetScreen';

// Follow-up service
import {
  fetchFollowUpMetrics,
  fetchFollowUpItems,
  FollowUpItem,
  FollowUpMetrics,
  FollowUpFilters
} from '../../../../components/FollowUps/FollowUpService';

// Date formatting
import { format } from 'date-fns';

// Navigation
import { useNavigate } from 'react-router-dom';

interface UserOption {
  id: string;
  name: string;
}

interface FollowUpCountProps {
  users: UserOption[];
  selectedUser: UserOption[];
  startTime: string | null;
  endTime: string | null;
  showTooltip?: boolean;
}

const FollowUpCount: React.FC<FollowUpCountProps> = ({ users, selectedUser, startTime, endTime, showTooltip = false }) => {

  const navigate = useNavigate();
  const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

  // Panel state
  const [isPanelOpen, setIsPanelOpen] = React.useState(false);
  const [selectedFollowUpType, setSelectedFollowUpType] = React.useState<string>('');
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [panelWidth, setPanelWidth] = React.useState(600); // Default width in pixels (increased from 384px)
  const [isResizing, setIsResizing] = React.useState(false);

  // Pagination state
  const [lastDocument, setLastDocument] = React.useState<any>(null);
  const [hasMore, setHasMore] = React.useState(false);

  // data state - using real data from service
  const [followUpMetrics, setFollowUpMetrics] = React.useState<FollowUpMetrics>({
    totalFollowUps: 0,
    upcomingFollowUps: 0,
    todaysFollowUps: 0,
    overdueFollowUps: 0
  });

  // Real follow-up items data
  const [followUpItems, setFollowUpItems] = React.useState<FollowUpItem[]>([]);

  // Fetch follow-up metrics
  React.useEffect(() => {
    const fetchMetrics = async () => {
      if (!userData?.companyId) return;

      try {
        setLoading(true);

        const filters: FollowUpFilters = {
          companyId: userData.companyId,
          userIds: selectedUser.length > 0 ? selectedUser.map(user => user.id) : undefined,
          startTime,
          endTime,
          currentScreen,
          isAdmin: isAdmin || false,
          isManager: isManager || false,
          isStaff: isStaff || false,
          currentUserId: currentUser?.uid
        };

        console.log("Follow-up filters applied:", filters);

        const metrics = await fetchFollowUpMetrics(filters);
        setFollowUpMetrics(metrics);
      } catch (error) {
        console.error("Error fetching follow-up metrics:", error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [userData?.companyId, startTime, endTime, selectedUser, currentScreen, isAdmin, isManager, isStaff]);

  const handleFollowUpClick = async (type: string) => {
    if (!userData?.companyId) return;

    setSelectedFollowUpType(type);
    setIsPanelOpen(true);

    // Reset pagination state
    setLastDocument(null);
    setHasMore(false);

    try {
      setLoading(true);

      const filters: FollowUpFilters = {
        companyId: userData.companyId,
        userIds: selectedUser.length > 0 ? selectedUser.map(user => user.id) : undefined,
        startTime,
        endTime,
        currentScreen,
        isAdmin: isAdmin || false,
        isManager: isManager || false,
        isStaff: isStaff || false,
        currentUserId: currentUser?.uid
      };

      const result = await fetchFollowUpItems(filters, type as 'upcoming' | 'today' | 'overdue', 100);
      setFollowUpItems(result.items);
      setLastDocument(result.lastDocument);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error(`Error fetching ${type} follow-up items:`, error);
      setFollowUpItems([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleShowMore = async () => {
    if (!userData?.companyId || !lastDocument || !hasMore) return;

    try {
      setLoadingMore(true);

      const filters: FollowUpFilters = {
        companyId: userData.companyId,
        userIds: selectedUser.length > 0 ? selectedUser.map(user => user.id) : undefined,
        startTime,
        endTime,
        currentScreen,
        isAdmin: isAdmin || false,
        isManager: isManager || false,
        isStaff: isStaff || false,
        currentUserId: currentUser?.uid
      };

      const result = await fetchFollowUpItems(
        filters,
        selectedFollowUpType as 'upcoming' | 'today' | 'overdue',
        100,
        lastDocument
      );

      // Append new items to existing ones
      setFollowUpItems(prev => [...prev, ...result.items]);
      setLastDocument(result.lastDocument);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error(`Error fetching more ${selectedFollowUpType} follow-up items:`, error);
    } finally {
      setLoadingMore(false);
    }
  };

  const closePanelHandler = () => {
    setIsPanelOpen(false);
    setPanelWidth(600); // Reset to default width when closing
    setTimeout(() => setSelectedFollowUpType(''), 300); // Clear type after animation
  };

      const handleStudentClick = (studentId: string) => {
        navigate(`/studentdetails/${studentId}`);
    };

  // Handle mouse down on resize handle
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle mouse move for resizing
  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      const minWidth = 300; // Minimum panel width
      const maxWidth = window.innerWidth * 0.8; // Maximum 80% of screen width

      setPanelWidth(Math.max(minWidth, Math.min(maxWidth, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };
  }, [isResizing]);

  const getFilteredFollowUps = () => {
    // Items are already filtered by type when fetched
    return followUpItems;
  };

  const getPanelTitle = () => {
    switch (selectedFollowUpType) {
      case 'upcoming':
        return 'Upcoming Follow Ups';
      case 'today':
        return "Today's Follow Ups";
      case 'overdue':
        return 'Overdue Follow Ups';
      default:
        return '';
    }
  };

  const getPanelCount = () => {
    switch (selectedFollowUpType) {
      case 'upcoming':
        return followUpMetrics?.upcomingFollowUps || 0;
      case 'today':
        return followUpMetrics?.todaysFollowUps || 0;
      case 'overdue':
        return followUpMetrics?.overdueFollowUps || 0;
      default:
        return 0;
    }
  };

  const followUpBoxes = [
    {
      title: 'Upcoming Follow Ups',
      count: followUpMetrics?.upcomingFollowUps || 0,
      bgColor: 'bg-blue-300',
      position: 'left',
      type: 'upcoming',
      tooltip: 'This section shows all upcoming follow-ups that are scheduled for future dates and times.'
    },
    {
      title: "Today's Follow Ups",
      count: followUpMetrics?.todaysFollowUps || 0,
      bgColor: 'hsl(204, 0%, 63%)',
      position: 'right',
      type: 'today',
      tooltip: "This section shows only today's upcoming follow-ups Count based on time.."
    },
    {
      title: 'Overdue Follow Ups',
      count: followUpMetrics?.overdueFollowUps || 0,
      bgColor: 'bg-red-500',
      position: 'left',
      type: 'overdue',
      tooltip: 'This section shows all follow-ups that are past their scheduled date and time and require immediate attention.'
    }
  ];

  return (
    <div className="relative">
      {/* Main Component */}
      <div className="bg-white rounded-2xl">
        <div className="relative group">
          <h2
            className="text-lg text-black font-medium text-center mb-8"
            style={{
              fontFamily: "Montserrat",
            }}
          >Total Follow-up Count</h2>

          {/* Tooltip */}
          {showTooltip && (
            <div className="absolute left-1/2 transform -translate-x-1/2 top-full mt-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10">
              This data includes all time periods and is not limited to the selected date range.
            </div>
          )}
        </div>

        <div className="space-y-8">
          {followUpBoxes.map((box, index) => (
            <div
              key={index}
              className="relative bg-gray-100 rounded-2xl h-16 cursor-pointer hover:bg-gray-200 transition-colors duration-200"
              onClick={() => handleFollowUpClick(box.type)}
              title={box.tooltip}
            >
              <div
                className={`absolute ${box.position === 'left' ? 'left-0 rounded-tl-full rounded-br-full' : 'right-0 rounded-tr-full rounded-bl-full'} top-0 h-full w-5 to-transparent ${box.bgColor.startsWith('bg-') ? box.bgColor : ''}`}
                style={!box.bgColor.startsWith('bg-') ? { backgroundColor: box.bgColor } : {}}
              />

              <div className="flex flex-col items-center justify-center h-full">
                <h3 className="text-sm font-medium text-center">{box.title}</h3>
                <p className="text-lg font-semibold mt-1">{box.count}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Total Summary */}
        <div className="mt-12 pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600">Total Follow-ups:</span>
            <span className="text-lg font-semibold text-cyan-600">{followUpMetrics?.totalFollowUps}</span>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isPanelOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={closePanelHandler}
        />
      )}

      {/* Right Side Panel */}
      <div
        className={`fixed top-0 right-0 h-full bg-white shadow-2xl z-50 transform transition-all duration-300 ease-in-out ${isPanelOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        style={{ width: `${panelWidth}px` }}
      >
        {/* Resize Handle */}
        <div
          className="absolute left-0 top-0 w-1 h-full bg-gray-300 hover:bg-blue-500 cursor-ew-resize z-10 transition-colors duration-200"
          onMouseDown={handleMouseDown}
          title="Drag to resize"
        />

        <div className="p-6 h-full flex flex-col">
          {/* Panel Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex flex-col">
              <h2 className="text-xl font-semibold text-gray-800" style={{ fontFamily: "Montserrat" }}>
                {getPanelTitle()}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Total: <span className="font-semibold text-blue-600">{getPanelCount()}</span> follow-ups
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={closePanelHandler}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading follow-ups...</span>
              </div>
            ) : getFilteredFollowUps().length > 0 ? (
              <div className={panelWidth > 600 ? "grid grid-cols-2 gap-4" : "space-y-4"}>
                {getFilteredFollowUps().map((item, index) => (
                  <div
                    key={item.id}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow duration-200"
                    style={{
                      animation: `slideIn 0.3s ease-out ${index * 0.1}s both`
                    }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-gray-800">{item.studentName}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                        item.status === 'today' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                        {item.status === 'today' ? 'Today' :
                          item.status === 'upcoming' ? 'Upcoming' : 'Overdue'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">📞 {item.studentNumber}</p>
                    <p className="text-sm text-gray-600 mb-2">📅 {format(item.followUpTime.toDate(), 'PPP p')}</p>
                    <p className="text-sm text-gray-600 mb-2">👤 Assigned to: {item.assignedUserName}</p>
                    <p className="text-sm text-gray-600 mb-2">🎯 Stage: {item.leadStage}</p>
                    <p className="text-sm text-gray-700 bg-white p-2 rounded border">
                      {item.followUp}
                    </p>
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => handleStudentClick(item.leadId)}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 border border-blue-600 rounded-md py-1 px-2"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-lg font-medium">No follow-ups found</p>
                <p className="text-sm mt-1">There are no {selectedFollowUpType} follow-ups at the moment.</p>
              </div>
            )}

            {/* Show More Button */}
            {!loading && hasMore && getFilteredFollowUps().length > 0 && (
              <div className="mt-4 text-center">
                <button
                  onClick={handleShowMore}
                  disabled={loadingMore}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {loadingMore ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading more...
                    </div>
                  ) : (
                    'Show More'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      {/* <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style> */}
    </div>
  );
};

export default FollowUpCount;